// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * session.ts — the live P2P match.
 *
 * Sporeline's netcode is unusual and deliberately so: there is NO shared
 * continuous state. Every peer owns exactly one Game — its own field — and no
 * peer ever simulates another's. Creeps, towers, gold and particles are all
 * local. That means the classic snapshot-star desync surface simply does not
 * exist here.
 *
 * What IS shared is small enough to list:
 *
 *   `wv`     the host says wave N starts now      (host -> all)
 *   `spore`  somebody sent a spore at somebody    (any -> all)
 *   `st`     my status, ~1Hz                      (any -> all)
 *   `fld`    my tower grid, ~2Hz, DISPLAY ONLY    (any -> all)
 *   `end`    the host calls the match             (host -> all)
 *
 * So the host owns three things and only three: the wave clock, the round seed
 * (which `rematch.ts` already carries), and the win check. That is also the
 * complete list of what a promoted host takes over — see `takeOver()`, which is
 * why host transfer here is nearly free and is unit-testable without a network.
 *
 * `fld` is display-only and must stay that way. It exists so you can watch a
 * rival's maze buckle; nothing in this file may ever read it for correctness.
 */

import type { Net, PeerId } from '@ben-gy/game-engine/net';

/** A channel's send handle, with the detach hook net.ts hands back. */
type Sender<T> = ((data: T, toPeers?: PeerId | PeerId[]) => void) & { off: () => void };
import type { RoundPlayer } from '@ben-gy/game-engine/rematch';
import { Game } from './game';
import type { Mode } from './modes';

/** Everything a results table or a rival strip needs about one player. */
export interface PeerStatus {
  id: PeerId;
  name: string;
  seat: number;
  heart: number;
  maxHeart: number;
  wave: number;
  gold: number;
  alive: boolean;
  kills: number;
  leaked: number;
  sent: number;
  received: number;
  towers: number;
  peakRoad: number;
  /** Packed tower grid — for the rival mini-field. Never read for logic. */
  field: string;
  /** True until we have actually heard from them. */
  stale: boolean;
}

interface StatusMsg {
  seat: number;
  heart: number;
  wave: number;
  gold: number;
  alive: boolean;
  kills: number;
  leaked: number;
  sent: number;
  received: number;
  towers: number;
  peakRoad: number;
}

interface SporeMsg {
  from: number;
  to: number;
}

interface WaveMsg {
  wave: number;
}

interface EndMsg {
  /** Seat index, or -1 for a draw. */
  winner: number;
}

export interface SessionConfig {
  net: Net;
  mode: Mode;
  seed: number;
  players: RoundPlayer[];
  selfId: PeerId;
  /** Repaint the HUD / rival strip. */
  onChange: () => void;
  /** The match is over — show the results table. */
  onEnd: (winner: number) => void;
  /** A spore landed on us, for the juice. */
  onSporeIn?: (fromSeat: number) => void;
  /** A new wave started, for the juice. */
  onWave?: (wave: number) => void;
}

/** Host broadcast cadence. Intervals, never rAF — a backgrounded host must
 *  keep the match advancing rather than freezing everyone else's round. */
const STATUS_MS = 1000;
const FIELD_MS = 500;

export class Session {
  readonly net: Net;
  readonly mode: Mode;
  readonly seed: number;
  readonly players: RoundPlayer[];
  readonly selfSeat: number;
  /** THIS peer's field. The only Game in the process. */
  readonly game: Game;

  wave = 0;
  over = false;
  winner = -1;

  private config: SessionConfig;
  private statuses = new Map<number, PeerStatus>();
  private timers: ReturnType<typeof setInterval>[] = [];
  private waveTimer: ReturnType<typeof setInterval> | null = null;
  private offs: (() => void)[] = [];

  private sendSpore: Sender<SporeMsg>;
  private sendStatus: Sender<StatusMsg>;
  private sendField: Sender<{ seat: number; f: string }>;
  private sendWave: Sender<WaveMsg>;
  private sendEnd: Sender<EndMsg>;

  constructor(config: SessionConfig) {
    this.config = config;
    this.net = config.net;
    this.mode = config.mode;
    this.seed = config.seed;
    this.players = config.players;
    this.selfSeat = Math.max(
      0,
      config.players.findIndex((p) => p.id === config.selfId),
    );
    this.game = new Game(config.mode, config.seed);

    for (let i = 0; i < config.players.length; i++) {
      this.statuses.set(i, {
        id: config.players[i].id,
        name: config.players[i].name,
        seat: i,
        heart: config.mode.heartBeats,
        maxHeart: config.mode.heartBeats,
        wave: 0,
        gold: config.mode.startGold,
        alive: true,
        kills: 0,
        leaked: 0,
        sent: 0,
        received: 0,
        towers: 0,
        peakRoad: 0,
        field: '',
        stale: i !== this.selfSeat,
      });
    }

    this.sendSpore = this.net.channel<SporeMsg>('spore', (m) => this.onSpore(m));
    this.sendStatus = this.net.channel<StatusMsg>('st', (m, from) => this.onStatus(m, from));
    this.sendField = this.net.channel<{ seat: number; f: string }>('fld', (m) => {
      const s = this.statuses.get(m.seat);
      if (s) s.field = String(m.f ?? '');
    });
    this.sendWave = this.net.channel<WaveMsg>('wv', (m, from) => this.onWave(m, from));
    this.sendEnd = this.net.channel<EndMsg>('end', (m, from) => this.onEnd(m, from));

    this.offs.push(
      () => this.sendSpore.off(),
      () => this.sendStatus.off(),
      () => this.sendField.off(),
      () => this.sendWave.off(),
      () => this.sendEnd.off(),
    );

    this.timers.push(setInterval(() => this.broadcastStatus(), STATUS_MS));
    this.timers.push(
      setInterval(() => this.sendField({ seat: this.selfSeat, f: this.game.packField() }), FIELD_MS),
    );

    if (this.net.isHost()) this.startHosting();
  }

  // ── host duties ───────────────────────────────────────────────────────────

  private startHosting(): void {
    if (this.waveTimer) return;
    // Wave 1 immediately, then one every mode.waveMs.
    if (this.wave === 0) this.beginWave(1);
    this.waveTimer = setInterval(() => this.beginWave(this.wave + 1), this.mode.waveMs);
  }

  private stopHosting(): void {
    if (this.waveTimer) clearInterval(this.waveTimer);
    this.waveTimer = null;
  }

  private beginWave(n: number): void {
    if (this.over) return;
    if (n > this.mode.waveCap) {
      this.callMatch();
      return;
    }
    this.sendWave({ wave: n });
    this.applyWave(n);
  }

  /**
   * The host decides when the match is over.
   *
   * It reads only `st`, which is why a promoted host can do this the instant it
   * is promoted: it has been receiving everyone's status all along.
   */
  private checkEnd(): void {
    if (this.over || !this.net.isHost()) return;
    const live = [...this.statuses.values()].filter((s) => s.alive);
    if (live.length <= 1 && this.statuses.size > 1) this.callMatch();
  }

  private callMatch(): void {
    if (this.over) return;
    const table = this.standings();
    const winner = table.length && table[0].alive ? table[0].seat : -1;
    this.sendEnd({ winner });
    this.finish(winner);
  }

  // ── receiving ─────────────────────────────────────────────────────────────

  private onWave(m: WaveMsg, from: PeerId): void {
    // Only the host's clock counts. A stale or forged wave from a demoted peer
    // must not drag everyone forward.
    if (from !== this.net.host()) return;
    this.applyWave(Number(m?.wave) || 0);
  }

  private applyWave(n: number): void {
    if (this.over || n <= this.wave) return;
    this.wave = n;
    if (n > 1) this.game.payIncome(this.bestRivalHeart());
    this.game.startWave(n);
    this.config.onWave?.(n);
    this.config.onChange();
  }

  private onSpore(m: SporeMsg): void {
    const to = Number(m?.to);
    const from = Number(m?.from);
    if (!Number.isInteger(to)) return;
    const target = this.statuses.get(to);
    if (target) target.received++;
    if (to === this.selfSeat) {
      this.game.receive('spore');
      this.config.onSporeIn?.(from);
    }
    this.config.onChange();
  }

  private onStatus(m: StatusMsg, from: PeerId): void {
    const seat = Number(m?.seat);
    const status = this.statuses.get(seat);
    // A peer may only speak for its own seat.
    if (!status || status.id !== from) return;
    status.heart = Number(m.heart) || 0;
    status.wave = Number(m.wave) || 0;
    status.gold = Number(m.gold) || 0;
    status.alive = Boolean(m.alive);
    status.kills = Number(m.kills) || 0;
    status.leaked = Number(m.leaked) || 0;
    status.sent = Number(m.sent) || 0;
    status.received = Number(m.received) || 0;
    status.towers = Number(m.towers) || 0;
    status.peakRoad = Number(m.peakRoad) || 0;
    status.stale = false;
    this.checkEnd();
    this.config.onChange();
  }

  private onEnd(m: EndMsg, from: PeerId): void {
    if (from !== this.net.host()) return;
    this.finish(Number(m?.winner ?? -1));
  }

  private finish(winner: number): void {
    if (this.over) return;
    this.over = true;
    this.winner = winner;
    this.stopHosting();
    this.config.onEnd(winner);
  }

  // ── sending ───────────────────────────────────────────────────────────────

  private broadcastStatus(): void {
    const me = this.selfStatus();
    this.sendStatus({
      seat: this.selfSeat,
      heart: me.heart,
      wave: me.wave,
      gold: me.gold,
      alive: me.alive,
      kills: me.kills,
      leaked: me.leaked,
      sent: me.sent,
      received: me.received,
      towers: me.towers,
      peakRoad: me.peakRoad,
    });
    this.checkEnd();
  }

  /** Pay for and launch a spore at a rival seat. Returns false if unaffordable. */
  spore(targetSeat: number): boolean {
    if (this.over || targetSeat === this.selfSeat) return false;
    const target = this.statuses.get(targetSeat);
    if (!target || !target.alive) return false;
    if (this.game.spendSend() === 0) return false;
    this.sendSpore({ from: this.selfSeat, to: targetSeat });
    target.received++;
    this.config.onChange();
    return true;
  }

  // ── host transfer ─────────────────────────────────────────────────────────

  /**
   * Wired to `onHostChange`. The promoted peer picks up the wave clock and the
   * win check and the match keeps escalating — and, crucially, can still END.
   *
   * There is no state to adopt, which is the payoff of the per-peer design: the
   * new host has been receiving everyone's `st` since the round began, so it
   * already knows every Heart. It resumes the clock from the wave number it is
   * on rather than restarting at 1, or a host transfer would replay the match.
   */
  onHostChange(isSelfHost: boolean): void {
    if (this.over) return;
    if (isSelfHost) {
      this.startHosting();
      this.checkEnd();
    } else {
      this.stopHosting();
    }
    this.config.onChange();
  }

  /** A peer vanished. Its seat stops counting towards "still alive". */
  onPeerLeave(id: PeerId): void {
    for (const s of this.statuses.values()) {
      if (s.id === id) s.alive = false;
    }
    this.checkEnd();
    this.config.onChange();
  }

  // ── views ─────────────────────────────────────────────────────────────────

  /** Our own row, read live from the Game rather than from the wire. */
  selfStatus(): PeerStatus {
    const s = this.statuses.get(this.selfSeat)!;
    s.heart = this.game.heart;
    s.wave = this.wave;
    s.gold = Math.floor(this.game.gold);
    s.alive = !this.game.over;
    s.kills = this.game.stats.kills;
    s.leaked = this.game.stats.leaked;
    s.sent = this.game.stats.sent;
    s.received = this.game.stats.received;
    s.towers = this.game.towers.size;
    s.peakRoad = this.game.stats.peakRoad;
    s.stale = false;
    return s;
  }

  all(): PeerStatus[] {
    this.selfStatus();
    return [...this.statuses.values()].sort((a, b) => a.seat - b.seat);
  }

  standings(): PeerStatus[] {
    return this.all()
      .slice()
      .sort(
        (a, b) =>
          Number(b.alive) - Number(a.alive) ||
          b.heart - a.heart ||
          b.wave - a.wave ||
          b.kills - a.kills ||
          a.seat - b.seat,
      );
  }

  private bestRivalHeart(): number {
    let best = 0;
    for (const s of this.statuses.values()) {
      if (s.seat === this.selfSeat || !s.alive) continue;
      if (s.heart > best) best = s.heart;
    }
    return best;
  }

  /** Advance our own field. Called from the render loop. */
  step(dtMs: number): void {
    if (this.over) return;
    this.game.step(dtMs);
    if (this.game.over) {
      this.selfStatus();
      this.broadcastStatus();
    }
  }

  destroy(): void {
    this.stopHosting();
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    for (const off of this.offs) {
      try {
        off();
      } catch {
        /* a channel already detached */
      }
    }
    this.offs = [];
  }
}
